// Shared helpers for the video portfolio pattern first built for workers (worker_portfolio),
// now also used by employer_portfolio and union_portfolio.

export function extractYouTubeId(url) {
  const m = (url || '').match(/(?:youtube\.com\/(?:watch\?.*v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

export function extractVimeoId(url) {
  const m = (url || '').match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
}

export function detectVideoType(url) {
  if (/youtube\.com|youtu\.be/.test(url)) return 'youtube';
  if (/vimeo\.com/.test(url)) return 'vimeo';
  return 'link';
}

// YouTube serves a stable still for every video; Vimeo needs an API call, so it gets none.
export function linkThumbnailUrl(url) {
  const ytId = extractYouTubeId(url);
  return ytId ? `https://i.ytimg.com/vi/${ytId}/hqdefault.jpg` : null;
}

// Grabs a representative frame from a local video file as a JPEG blob, entirely client-side.
// Resolves null (never rejects) when the browser can't decode the file (e.g. some .mov/.avi
// codecs) -- a missing thumbnail just means the player shows its own first frame instead.
export function captureVideoThumbnail(file, { maxWidth = 640, timeoutMs = 8000 } = {}) {
  return new Promise((resolve) => {
    let settled = false;
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement('video');
    const finish = (blob) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      URL.revokeObjectURL(objectUrl);
      video.removeAttribute('src');
      video.load();
      resolve(blob);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);

    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.onerror = () => finish(null);
    let seekTarget = 0;
    video.onloadedmetadata = () => {
      // ~10% in (capped at 1s) skips black lead-in frames without landing mid-action. Files
      // with no known duration (e.g. MediaRecorder WebM reports Infinity) use the first frame,
      // since seeking to where the element already is may never fire 'seeked'.
      seekTarget = Number.isFinite(video.duration) && video.duration > 0 ? Math.min(1, video.duration * 0.1) : 0;
      if (seekTarget > 0) video.currentTime = seekTarget;
    };
    video.onloadeddata = () => { if (seekTarget === 0) capture(); };
    video.onseeked = () => capture();
    const capture = () => {
      try {
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (!w || !h) { finish(null); return; }
        const scale = Math.min(1, maxWidth / w);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(w * scale);
        canvas.height = Math.round(h * scale);
        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(blob => finish(blob), 'image/jpeg', 0.82);
      } catch (_) {
        finish(null);
      }
    };
    video.src = objectUrl;
  });
}

// supabase-js storage.upload() exposes no progress events, which matters for multi-MB video
// files -- this is the same authenticated POST it makes, over XHR so the bar reflects real bytes.
export function uploadWithProgress({ supabaseUrl, anonKey, accessToken, bucket, path, file, contentType, onProgress }) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    const encodedPath = path.split('/').map(encodeURIComponent).join('/');
    xhr.open('POST', `${supabaseUrl}/storage/v1/object/${bucket}/${encodedPath}`);
    xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
    xhr.setRequestHeader('apikey', anonKey);
    xhr.setRequestHeader('x-upsert', 'false');
    xhr.setRequestHeader('Content-Type', contentType || 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) { resolve({ error: null }); return; }
      let message = `Upload failed (${xhr.status})`;
      try { message = JSON.parse(xhr.responseText)?.message || message; } catch (_) {}
      resolve({ error: { status: xhr.status, message } });
    };
    xhr.onerror = () => resolve({ error: { status: 0, message: 'Network error' } });
    xhr.send(file);
  });
}

// Storage object path from a public URL in the given bucket (for best-effort cleanup on delete).
export function storagePathFromPublicUrl(url, bucket) {
  if (!url) return null;
  const marker = `/storage/v1/object/public/${bucket}/`;
  const idx = url.indexOf(marker);
  if (idx === -1) return null;
  return decodeURIComponent(url.slice(idx + marker.length).split('?')[0]);
}
